import {Injectable} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erNil} from "../støttefunksjoner/nilsjekk";
import {Intl, Temporal} from "temporal-polyfill";
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {GjeldendeLokale} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import ZonedDateTime = Temporal.ZonedDateTime;
import PlainTime = Temporal.PlainTime;
import PlainDate = Temporal.PlainDate;
import Duration = Temporal.Duration;
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;

const debug = initDebugger('TemporalFormateringTjeneste');

/** ente en av strengene  'full', 'long', 'medium', 'short' eller et {@link DateTimeFormatOptions} objekt */
export type ZonedDateTimeFormat = DateTimeFormatOptions['dateStyle'] | DateTimeFormatOptions;
/** ente en av strengene  'full', 'long', 'medium', 'short' eller et {@link DateTimeFormatOptions} objekt */
export type PlainDateFormat = DateTimeFormatOptions['dateStyle'] | DateTimeFormatOptions;
/** en av strengene  'full', 'long', 'medium', 'short' */
export type PlainTimeFormat = DateTimeFormatOptions['timeStyle'];
export type DurationFormat = 'timerMinutterStandard' | 'timerMinutterKort' | 'timerMinutterSekunderStandard' | 'timerMinutterSekunderKort';

/**
 * Tjeneste for formatering av typer fra js Temporal-apiet.
 */
@Injectable()
export class TemporalFormatering {

    constructor(private gjeldendeLokale: GjeldendeLokale, private gjeldendeTidssone: GjeldendeTidssone, private oversettelser: Oversettelser, private klokke: Klokke) {
    }

    /**
     * Formaterer ZonedDateTime instanser til brukerrettet streng-representasjon.
     * Dersom datoOgKlokkeslett er null returneres tom streng.
     * Formatet 'short' gir '11.07.2014, 12:01', 'medium' gir '11. juli 2014, 12:01' og 'long' gir 'fredag 11. juli 2014 kl. 12:01'.
     * 'long' og 'full' skriver ut tidssoneinformasjon, og da bør tidssone argumentet angis.
     *
     * Se TemporalFormatering.jestspec.ts for formateringseksempler
     */
    formaterZonedDateTime(datoOgKlokkeslett: ZonedDateTime | null, format: ZonedDateTimeFormat = 'short', visTidssone = false, tidssone = this.gjeldendeTidssone.brukersTidssone().somZoneId()): string {
        if (erNil(datoOgKlokkeslett)) return '';
        const formatObjekt: DateTimeFormatOptions = erStreng(format) ? {dateStyle: format as DateTimeFormatOptions['dateStyle'], timeStyle: 'short'} : format as DateTimeFormatOptions
        const formatert = datoOgKlokkeslett
          .withTimeZone(tidssone)
          .toLocaleString(this.gjeldendeLokale.brukersLokale().somLocaleId(), formatObjekt);
        return visTidssone ? `${formatert} (${tidssone})` : formatert;
    }

    /**
     * Formaterer ZonedDateTime relativt til 'nå' som streng.
     * <pre>
     * Dersom datoOgKlokkeslett er null returneres tom streng.
     *
     * Nå på samme dag
     * 1 minutt siden
     * 10 minutter side
     * 1 time siden
     * 3 timer siden
     *
     * når +/- 1 dag
     * i går kl 12:00
     * i morgen kl 12:00
     *
     * når mer enn 1 dag +/-
     * 12.02.2023 kl 12:00
     * når mer enn 1 dag +/- benyttes angitt format, se {@link #formaterZonedDateTime()}
     * </pre>
     */
    formaterZonedDateTimeRelativ(datoOgKlokkeslett: ZonedDateTime | null, format: ZonedDateTimeFormat = 'short', tidssone = this.gjeldendeTidssone.brukersTidssone().somZoneId()): string {
        if (erNil(datoOgKlokkeslett)) return '';

        const nå = this.klokke.nåSomZonedDateTime()
        const varighet = nå.until(datoOgKlokkeslett)
        const antallDager = nå.toPlainDate().until(datoOgKlokkeslett.toPlainDate()).round({largestUnit: 'days'}).days;

        //når på samme dag
        if (antallDager == 0) {
            const avrundet = varighet.round({largestUnit: "auto"})
            if (avrundet.hours == 1) return this.oversettelser.oversett(i18n('om {timer} time'), {timer: avrundet.hours});
            if (avrundet.hours > 0) return this.oversettelser.oversett(i18n('om {timer} timer'), {timer: avrundet.hours});
            if (avrundet.hours == -1) return this.oversettelser.oversett(i18n('{timer} time siden'), {timer: Math.abs(avrundet.hours)});
            if (avrundet.hours < 0) return this.oversettelser.oversett(i18n('{timer} timer siden'), {timer: Math.abs(avrundet.hours)});

            if (avrundet.minutes == 1) return this.oversettelser.oversett(i18n('om {minutter} minutt'), {minutter: avrundet.minutes});
            if (avrundet.minutes > 0) return this.oversettelser.oversett(i18n('om {minutter} minutter'), {minutter: avrundet.minutes});
            if (avrundet.minutes == -1) return this.oversettelser.oversett(i18n('{minutter} minutt siden'), {minutter: Math.abs(avrundet.minutes)});
            if (avrundet.minutes < 0) return this.oversettelser.oversett(i18n('{minutter} minutter siden'), {minutter: Math.abs(avrundet.minutes)});

            return this.oversettelser.oversett(i18n('nå nettopp'));
        }

        //i går/ i morgen
        const klokkeslett = datoOgKlokkeslett.toPlainTime().toLocaleString(this.gjeldendeLokale.brukersLokale().somLocaleId(), {timeStyle: 'short'});
        if (antallDager == 1)
            return this.oversettelser.oversett(i18n('i morgen kl {klokkeslett}'), {klokkeslett});
        if (antallDager == -1)
            return this.oversettelser.oversett(i18n('i går kl {klokkeslett}'), {klokkeslett});

        //mer enn +/- 1 dag
        return this.formaterZonedDateTime(datoOgKlokkeslett, format, false, tidssone);
    }

    /**
     * Formaterer PlainDate relativt til 'nå' som streng.
     * <pre>
     * Dersom PlainDate er null returneres tom streng.
     *
     * Nå på samme dag
     * i dag
     *
     * når +/- 1 dag
     * i går
     * i morgen
     *
     * når mer enn +/- 1 dag
     * om N dager
     * for N dager siden
     *
     * når mer enn 10 dag +/-
     * 12.02.2023
     * når mer enn 10 dag +/- benyttes angitt format, se {@link #formaterZonedDateTime()}
     * </pre>
     *
     * @param dato datoen som skal formateres
     * @param format formatet som skal benyttes dersom dato ikke formateres som relativ (dvs antall dager siden 'nå' > maksRelativOffset), short som standard
     * @param maksRelativOffset dersom antall dager siden 'nå' er større enn dette tallet formateres datoen iht angitt format, 10 som standard
     * @param tidssone tidssone som skal benyttes ved formatering av dato, benytter tidssone satt på bruker som standard
     */
    formaterPlainDateRelativ(dato: PlainDate | null, format: PlainDateFormat = 'short', maksRelativOffset = 10, tidssone = this.gjeldendeTidssone.brukersTidssone()): string {
        if (erNil(dato)) return '';

        const nå = this.klokke.nåSomPlainDate(tidssone)
        const antallDager = nå.until(dato).round({largestUnit: 'days'}).days;

        //når på samme dag
        if (antallDager > maksRelativOffset || antallDager < -maksRelativOffset)
            return this.formaterPlainDate(dato, format);
        if (antallDager > 1)
            return this.oversettelser.oversett(i18n('om {antall} dager'), {antall: antallDager});
        if (antallDager < -1)
            return this.oversettelser.oversett(i18n('for {antall} dager siden'), {antall: Math.abs(antallDager)});
        if (antallDager == 1)
            return this.oversettelser.oversett(i18n('i morgen'));
        if (antallDager == -1)
            return this.oversettelser.oversett(i18n('i går'));
        if (antallDager == 0)
            return this.oversettelser.oversett(i18n('i dag'));

        //hit skal vi aldri komme..
        return '';
    }

    /**
     * Formaterer PlainTime instanser til brukerrettet streng-representasjon.
     * Dersom klokkeslett er null returneres tom streng.
     * Formatet 'short' gir hh:mm, 'medium' gir hh:mm:ss.
     * 'long' og 'full' skriver ut tidssoneinformasjon, og da bør tidssone argumentet angis.
     *
     * Se TemporalFormatering.jestspec.ts for formateringseksempler
     */
    formaterPlainTime(klokkeslett: PlainTime | null, format: PlainTimeFormat = 'short', tidssone = 'UTC'): string {
        if (erNil(klokkeslett)) return '';
        return klokkeslett.toLocaleString(this.gjeldendeLokale.brukersLokale().somLocaleId(), {timeStyle: format, timeZone: tidssone});
    }

    /**
     * Formaterer PlainDate instanser til brukerrettet streng-representasjon.
     * Dersom dato er null returneres tom streng.
     *
     * Se TemporalFormatering.jestspec.ts for formateringseksempler
     */
    formaterPlainDate(dato: PlainDate | null, format: PlainDateFormat = 'short'): string {
        if (erNil(dato)) return ''
        const formatOptions: Intl.DateTimeFormatOptions = erStreng(format) ? {dateStyle: format} : format;
        return dato.toLocaleString(this.gjeldendeLokale.brukersLokale().somLocaleId(), formatOptions);
    }

    /**
     * Formaterer Duration instanser til brukerrettet streng-representasjon.
     * Se TemporalFormatering.jestspec.ts for formateringseksempler
     */
    formaterDuration(varighet: Duration | null, format: DurationFormat = 'timerMinutterStandard'): string {
        if (erNil(varighet)) return '';
        /*
       MERK - fra https://tc39.es/proposal-temporal/docs/duration.html#toLocaleString
       Vedrørende bruk av Duration.toLocaleString(..):
       This method requires that your JavaScript environment supports Intl.DurationFormat.
       That is still an early-stage proposal and at the time of writing it is not supported anywhere.
       If Intl.DurationFormat is not available, then the output of this method is the same as that of duration.toString(),
       and the locales and options arguments are ignored.
        */
        const avrundet = varighet.round({largestUnit: 'hour'});

        if (avrundet.hours == 0 && this.medSekunder(format)) {
            const avrundetTilSekunder = varighet.round({largestUnit: 'hour', smallestUnit: 'second'});
            if (avrundetTilSekunder.hours == 0 && avrundetTilSekunder.seconds != 0) {
                const sekunder = `${avrundetTilSekunder.seconds}`.padStart(2, '0');
                return avrundetTilSekunder.minutes == 0 ?
                  `${avrundetTilSekunder.seconds} ${this.sekunderLabel(avrundetTilSekunder.seconds != 1, format)}` :
                  `${avrundetTilSekunder.minutes}:${sekunder} ${this.minutterLabel(true, format)}`;
            }
        }

        if (avrundet.hours == 0)
            return `${avrundet.minutes} ${this.minutterLabel(avrundet.minutes != 1, format)}`;

        const minutter = `${avrundet.minutes}`.padStart(2, '0');
        if (avrundet.hours == 1)
            return avrundet.minutes == 0 ?
              `${avrundet.hours} ${this.timerLabel(false, format)}` :
              `${avrundet.hours}:${minutter} ${this.timerLabel(true, format)}`;

        return avrundet.minutes == 0 ?
          `${avrundet.hours} ${this.timerLabel(true, format)}` :
          `${avrundet.hours}:${minutter} ${this.timerLabel(true, format)}`;
    }

    // private hjelpemetoder

    private medSekunder(format: DurationFormat) {
        return format == 'timerMinutterSekunderStandard' || format == 'timerMinutterSekunderKort';
    }

    private sekunderLabel(flertall: boolean, format: DurationFormat) {
        switch (format) {
            case 'timerMinutterKort':
            case 'timerMinutterSekunderKort':
                return this.oversettelser.oversett(i18n('sek'))
            default:
                return flertall ? this.oversettelser.oversett(i18n('sekunder')) : this.oversettelser.oversett(i18n('sekund'))
        }
    }

    private minutterLabel(flertall: boolean, format: DurationFormat) {
        switch (format) {
            case 'timerMinutterKort':
            case 'timerMinutterSekunderKort':
                return this.oversettelser.oversett(i18n('min'))
            default:
                return flertall ? this.oversettelser.oversett(i18n('minutter')) : this.oversettelser.oversett(i18n('minutt'))
        }
    }

    private timerLabel(flertall: boolean, format: DurationFormat) {
        switch (format) {
            case 'timerMinutterKort':
            case 'timerMinutterSekunderKort':
                return this.oversettelser.oversett(i18n('t'))
            default:
                return flertall ? this.oversettelser.oversett(i18n('timer')) : this.oversettelser.oversett(i18n('time'))
        }
    }

}
